import { useEffect, useMemo, useState } from 'react';
// Components
import { SingleSelection, sortSelectOptions } from '../../components/Select';
import NewLineOfBusinessModal from 'pages/Onboarding/NewLineOfBusinessModal';
import { useQuery } from 'react-query';
import { fetchAvailableData } from '../../api';
// Types
import { NewClientData, NewLOBData } from 'types';
import NewClientModal from 'pages/Onboarding/NewClientModal';
import { DEFAULT_FORM_STATE } from './Onboard';

// Type for the Add LOB (Line of business) Modal/form state
type AddClientAddLOBModalState = {
  agency: SingleSelection;
  client: SingleSelection;
  lob: SingleSelection;
};

const DEFAULT_ADD_LOB_ADD_CLIENT_MODAL_STATE: AddClientAddLOBModalState = {
  agency: null,
  client: null,
  lob: null,
};

const sortOptionsFunc = (
  a: Agency | Region | Market | LineOfBusiness,
  b: Agency | Region | Market | LineOfBusiness
) => {
  if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
  if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
  return 0;
};

function BaseSetup({ formData, setFormData }: BaseSetupProps) {
  const [clients, setClients] = useState<Client[]>([]);
  const [linesOfBusiness, setLinesOfBusiness] = useState<LineOfBusiness[]>([]);
  const [markets, setMarkets] = useState<Market[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [addClientAddLOBModalState, setAddClientAddLOBModalState] = useState({
    ...DEFAULT_ADD_LOB_ADD_CLIENT_MODAL_STATE,
  });
  const [addLOB, setAddLOB] = useState<boolean>(false);
  const [addClient, setAddClient] = useState(false);

  const { data: agencies = [], refetch } = useQuery({
    queryKey: ['base_setup_data'],
    queryFn: fetchAvailableData,
  });

  /* =============================================
   Handlers for Add LOB and Add Client Modal State
   =============================================== */

  // Agency Options
  const agencyOptions = useMemo(() => {
    if (!Array.isArray(agencies)) return [];
    const options = agencies.map(({ name, id }) => ({
      label: name,
      value: id.toString(),
    }));
    return sortSelectOptions(options);
  }, [agencies]);

  // Client Data & Options
  const { clientOptions } = useMemo(() => {
    if (!Array.isArray(agencies)) {
      return {
        clients: [],
        clientOptions: [],
      };
    }

    const selectedAgency = agencies.find(
      (agency) =>
        agency.id === parseInt(addClientAddLOBModalState.agency?.value || '')
    );

    const options =
      selectedAgency?.clients?.map(({ name, id }) => ({
        label: name,
        value: id.toString(),
      })) || [];

    return {
      clients: selectedAgency?.clients || [],
      clientOptions: sortSelectOptions(options),
    };
  }, [addClientAddLOBModalState.agency, agencies]);

  // Handle Agency selection
  const onChangeAgency = (selection: SingleSelection) => {
    setAddClientAddLOBModalState({
      ...DEFAULT_ADD_LOB_ADD_CLIENT_MODAL_STATE,
      agency: selection,
    });
  };

  // Handle Client selection
  const onChangeClient = (selection: SingleSelection) => {
    setAddClientAddLOBModalState((prev) => ({
      ...DEFAULT_ADD_LOB_ADD_CLIENT_MODAL_STATE,
      agency: prev.agency,
      client: selection,
    }));
  };

  // Handle Line of Business selection
  const onChangeLOB = (selection: SingleSelection) => {
    setAddClientAddLOBModalState((prev) => ({
      ...prev,
      lob: selection,
    }));
  };

  // Handle opening of AddLOB Modal
  const onClickAddLOB = () => {
    setAddLOB(true);
    if (addClientAddLOBModalState.lob) {
      onChangeLOB(null);
    }
  };

  const onSubmitLOB = (newLOB: NewLOBData | null) => {
    if (newLOB) {
      refetch();
      onChangeLOB({ label: newLOB.name, value: newLOB.id.toString() });
      // Update the Agency and Client in the main form's state - formData
      setFormData((prev) => ({
        ...prev,
        agency: addClientAddLOBModalState?.agency?.value ?? '',
        client: addClientAddLOBModalState?.client?.value ?? '',
        lineOfBusiness: newLOB.id.toString() ?? '',
      }));
    }
    setAddLOB(false);
  };

  // Handle Add Client
  const onSubmitClient = async (newClient: NewClientData | null) => {
    if (newClient) {
      onChangeClient({ label: newClient.name, value: newClient.id.toString() });
      // Update the selected Agency to the main form state
      setFormData((prev) => ({
        ...DEFAULT_FORM_STATE,
        agency: addClientAddLOBModalState?.agency?.value ?? '',
        client: newClient.id.toString() ?? '',
        new_business: prev.new_business,
      }));

      setAddClientAddLOBModalState((prev) => ({
        ...prev,
        agency: { label: newClient.name, value: newClient.id.toString() },
      }));
      refetch();
    }
    setAddClient(false);
  };

  const onClickAddClient = () => {
    setAddClient(true);
    if (addClientAddLOBModalState.client) {
      onChangeClient(null);
    }
  };

  /* ===== End of Add Client Add LOB Modal Handlers ===== */

  useEffect(() => {
    if (formData.agency) {
      const selectedAgency = agencies.find(
        (agency) => agency.id === Number(formData.agency)
      );
      // Update the agency state for the NewLineOfBusinessModal component
      setAddClientAddLOBModalState({
        ...DEFAULT_ADD_LOB_ADD_CLIENT_MODAL_STATE,
        agency: selectedAgency
          ? { label: selectedAgency.name, value: selectedAgency.id.toString() }
          : null,
      });

      if (selectedAgency) {
        setClients(selectedAgency.clients);
      }
      setErrors((prevErrors) => ({ ...prevErrors, agency: '' }));
    } else {
      setClients([]);
      setErrors((prevErrors) => ({
        ...prevErrors,
        agency: 'Agency is required.',
      }));
    }
  }, [formData.agency, agencies]);

  useEffect(() => {
    if (formData.client) {
      const selectedClient = clients.find(
        (client) => client.id === Number(formData.client)
      );
      // Update the client state for the NewLineOfBusinessModal component
      setAddClientAddLOBModalState((prev) => ({
        ...prev,
        client: selectedClient
          ? { label: selectedClient.name, value: selectedClient.id.toString() }
          : null,
        lob: null,
      }));
      if (selectedClient) {
        setLinesOfBusiness(selectedClient.lines_of_business || []);
        // If no LOB, use available markets at the client level
        setMarkets(selectedClient.available_markets || []);
      }
      setErrors((prevErrors) => ({ ...prevErrors, client: '' }));
    } else {
      setLinesOfBusiness([]);
      setMarkets([]);
      if (formData.agency) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          client: 'Client is required.',
        }));
      }
    }
  }, [formData.client, formData.agency, clients]);

  useEffect(() => {
    if (formData.lineOfBusiness) {
      const selectedLOB = linesOfBusiness.find(
        (lob) => lob.id === Number(formData.lineOfBusiness)
      );
      if (selectedLOB) {
        setMarkets(selectedLOB.available_markets || []);
      }
      setErrors((prevErrors) => ({ ...prevErrors, lineOfBusiness: '' }));
    } else {
      // If no LOB is selected, use client-level markets
      const selectedClient = clients.find(
        (client) => client.id === Number(formData.client)
      );
      if (selectedClient) {
        setMarkets(selectedClient.available_markets || []);
      }
    }
  }, [formData.lineOfBusiness, linesOfBusiness, clients, formData.client]);

  useEffect(() => {
    if (formData.market) {
      const selectedMarket = markets.find(
        (market) => market.name === formData.market
      );
      if (selectedMarket) {
        setRegions([selectedMarket.region]);
        setFormData((prevState) => ({
          ...prevState,
          region: selectedMarket.region.name,
        }));
      }
      setErrors((prevErrors) => ({ ...prevErrors, market: '' }));
    } else {
      setRegions([]);
      setErrors((prevErrors) => ({
        ...prevErrors,
        market: 'Market is required.',
      }));
    }
  }, [formData.market, setFormData, markets]);

  useEffect(() => {
    if (formData.region) {
      setErrors((prevErrors) => ({ ...prevErrors, region: '' }));
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        region: 'Region is required.',
      }));
    }
  }, [formData.region]);

  const onChange = (
    evt: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = evt.target;
    setFormData((prevState) => ({ ...prevState, [name]: value }));
  };

  // JSX
  return (
    <div>
      <h2 className="text-[32px] font-bold">Base Setup</h2>
      <h3 className="font-bold text-grayscale70 mt-2">Initial configuration</h3>
      <form className="mt-6 space-y-6">
        <div>
          <label className="block font-bold text-primarygray text-2xl mb-2">
            Agency
          </label>
          <select
            name="agency"
            className="w-full border border-gray200 rounded p-2 text-gray600 text-sm disabled:cursor-not-allowed"
            value={formData.agency}
            onChange={onChange}
          >
            <option value="">Select</option>
            {Array.isArray(agencies) &&
              agencies
                .slice()
                .sort(sortOptionsFunc)
                .map((agency) => (
                  <option key={agency.id} value={agency.id}>
                    {agency.name}
                  </option>
                ))}
          </select>
          {errors.agency && (
            <span className="text-error text-sm">{errors.agency}</span>
          )}
        </div>
        <div className="relative">
          <button
            type="button"
            className="absolute translate-y-2 top-0 right-0  border border-solid border-gray400 text-gray400 text-sm px-2 rounded-full flex items-center gap-1"
            onClick={onClickAddClient}
          >
            <span className="text-xl leading-none">+</span>Add
          </button>
          <div className="flex justify-between items-center">
            <label className="font-bold text-primarygray text-2xl mb-2">
              Client
            </label>
          </div>
          <select
            name="client"
            className="w-full border border-gray200 rounded p-2 text-gray600 text-sm disabled:cursor-not-allowed"
            value={formData.client}
            onChange={onChange}
          >
            <option value="">Select</option>
            {Array.isArray(clients) &&
              clients
                .slice()
                .sort(sortOptionsFunc)
                .map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
          </select>
          {errors.client && (
            <span className="text-error text-sm">{errors.client}</span>
          )}
        </div>

        <div className="relative">
          <button
            type="button"
            className="absolute translate-y-2 top-0 right-0  border border-solid border-gray400 text-gray400 text-sm px-2 rounded-full flex items-center gap-1"
            onClick={onClickAddLOB}
          >
            <span className="text-xl leading-none">+</span>Add
          </button>
          <label className="block font-bold text-primarygray text-2xl mb-2">
            Line of Business
          </label>
          <select
            name="lineOfBusiness"
            className="w-full border border-gray200 rounded p-2 text-gray600 text-sm"
            value={formData.lineOfBusiness}
            onChange={onChange}
          >
            <option value="">Select</option>
            {Array.isArray(linesOfBusiness) &&
              linesOfBusiness
                .slice()
                .sort(sortOptionsFunc)
                .map((lob) => (
                  <option key={lob.id} value={lob.id}>
                    {lob.name}
                  </option>
                ))}
          </select>
        </div>

        {/* --- Add Client Modal --- */}
        {addClient ? (
          <NewClientModal
            agencyOptions={agencyOptions}
            agencyValue={addClientAddLOBModalState.agency}
            onChangeAgency={onChangeAgency}
            onCancel={() => setAddClient(false)}
            onSubmit={onSubmitClient}
          />
        ) : null}

        {/* --- Add LOB Modal --- */}
        {addLOB ? (
          <NewLineOfBusinessModal
            agencyOptions={agencyOptions}
            agencyValue={addClientAddLOBModalState.agency}
            clientOptions={clientOptions}
            clientValue={addClientAddLOBModalState.client}
            onChangeAgency={onChangeAgency}
            onChangeClient={onChangeClient}
            onCancel={() => setAddLOB(false)}
            onSubmit={onSubmitLOB}
          />
        ) : null}

        <div>
          <label className="block font-bold text-primarygray text-2xl mb-2">
            Market
          </label>
          <select
            name="market"
            className="w-full border border-gray200 rounded p-2 text-gray600 text-sm"
            value={formData.market}
            onChange={onChange}
          >
            <option value="">Select</option>
            {Array.isArray(markets) &&
              markets
                .slice()
                .sort(sortOptionsFunc)
                .map((market) => (
                  <option key={market.id} value={market.name}>
                    {market.name}
                  </option>
                ))}
          </select>
          {errors.market && (
            <span className="text-error text-sm">{errors.market}</span>
          )}
        </div>
        <div>
          <label className="block font-bold text-primarygray text-2xl mb-2">
            Region
          </label>
          <select
            name="region"
            className="w-full border border-gray200 rounded p-2 text-gray600 text-sm"
            value={formData.region}
            onChange={onChange}
            disabled={true}
          >
            {Array.isArray(regions) &&
              regions
                .slice()
                .sort(sortOptionsFunc)
                .map((region) => (
                  <option key={region.id} value={region.name}>
                    {region.name}
                  </option>
                ))}
          </select>
          {errors.region && (
            <span className="text-error text-sm">{errors.region}</span>
          )}
        </div>
        {formData.new_business ? (
          <div className="inline-flex items-center mt-4 cursor-not-allowed">
            <input
              type="checkbox"
              name="new_business"
              className="w-3 h-3 cursor-not-allowed"
              checked={formData.new_business}
              disabled
            />
            <label className="ml-2 text-thirdgray cursor-not-allowed">
              Is New Business
            </label>
          </div>
        ) : null}
      </form>
    </div>
  );
}

export default BaseSetup;
